/**
 * ThreadOutline - Collapsible sidebar listing all messages in a thread
 *
 * Shows role badges, truncated text, click-to-jump.
 * Includes a toggleable image gallery for all media in the conversation.
 * Optionally shows cross-source related threads (M4C).
 */

import { useState, useEffect, useMemo } from 'react';
import type { ContentItem } from '../../contexts/BufferSyncContext';
import { resolveMediaUrl } from '../../utils/media-url';

const ROLE_ICONS: Record<string, string> = {
  user: '\u{1F464}',
  assistant: '\u{1F916}',
  system: '\u{2699}\uFE0F',
  tool: '\u{1F527}',
};

interface MediaRef {
  id: string;
  type: string;
  mimeType?: string;
  size?: number;
  dimensions?: { width: number; height: number };
}

export interface RelatedThread {
  id: string;
  title: string;
  sourceType: string;
  score: number;
  preview: string;
  threadRootId?: string;
}

interface ThreadOutlineProps {
  messages: ContentItem[];
  activeIndex: number;
  filteredIndices: number[];
  onJump: (index: number) => void;
  relatedThreads?: RelatedThread[];
  relatedLoading?: boolean;
  onOpenRelated?: (threadRootId: string) => void;
}

export function ThreadOutline({
  messages,
  activeIndex,
  filteredIndices,
  onJump,
  relatedThreads,
  relatedLoading,
  onOpenRelated,
}: ThreadOutlineProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('humanizer:outline-collapsed') === 'true'; }
    catch { return false; }
  });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('humanizer:outline-collapsed', String(collapsed)); }
    catch { /* noop */ }
  }, [collapsed]);

  // Collect all unique image refs from all messages in this thread.
  // Dedup by resolved URL to handle different ID formats for the same file
  // (e.g., "file-service://file-abc" vs bare "file-abc" from attachments).
  const imageRefs = useMemo(() => {
    const seen = new Set<string>();
    const refs: Array<MediaRef & { messageIndex: number }> = [];
    for (let i = 0; i < messages.length; i++) {
      const mediaRefs = messages[i].metadata.mediaRefs as MediaRef[] | undefined;
      if (!mediaRefs?.length) continue;
      for (const ref of mediaRefs) {
        if (ref.type !== 'image') continue;
        const key = resolveMediaUrl(ref.id);
        if (seen.has(key)) continue;
        seen.add(key);
        refs.push({ ...ref, messageIndex: i });
      }
    }
    return refs;
  }, [messages]);

  if (collapsed) {
    return (
      <div className="thread-outline thread-outline--collapsed">
        <button
          className="thread-outline__toggle"
          onClick={() => setCollapsed(false)}
          aria-label="Expand outline"
        >
          ›
        </button>
      </div>
    );
  }

  const filteredSet = new Set(filteredIndices);

  return (
    <div className="thread-outline">
      <div className="thread-outline__header">
        <span className="thread-outline__title">Outline</span>
        <button
          className="thread-outline__toggle"
          onClick={() => setCollapsed(true)}
          aria-label="Collapse outline"
        >
          ‹
        </button>
      </div>

      {/* Image gallery toggle */}
      {imageRefs.length > 0 && (
        <>
          <button
            className="thread-outline__gallery-toggle"
            onClick={() => setGalleryOpen(!galleryOpen)}
            aria-expanded={galleryOpen}
          >
            <span className="thread-outline__gallery-chevron">{galleryOpen ? '▾' : '▸'}</span>
            <span className="thread-outline__gallery-label">
              Images ({imageRefs.length})
            </span>
          </button>
          {galleryOpen && (
            <div className="thread-outline__gallery">
              {imageRefs.map((ref) => {
                const dims = ref.dimensions
                  ? `${ref.dimensions.width}×${ref.dimensions.height}`
                  : '';
                const sizeStr = ref.size
                  ? `${(ref.size / 1024).toFixed(0)} KB`
                  : '';
                const tooltip = [
                  ref.id.replace(/^file-service:\/\//, '').replace(/^sediment:\/\//, ''),
                  ref.mimeType || 'image',
                  dims,
                  sizeStr,
                  `Message #${ref.messageIndex + 1}`,
                ].filter(Boolean).join(' · ');

                return (
                  <button
                    key={ref.id}
                    className="thread-outline__gallery-thumb"
                    onClick={() => onJump(ref.messageIndex)}
                    title={tooltip}
                  >
                    <img
                      src={resolveMediaUrl(ref.id)}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        const el = e.currentTarget;
                        el.style.display = 'none';
                        el.parentElement?.classList.add('thread-outline__gallery-thumb--broken');
                      }}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="thread-outline__list">
        {messages.map((msg, i) => {
          const role = msg.metadata.authorRole ?? 'unknown';
          const icon = ROLE_ICONS[role] ?? '\u{1F4C4}';
          const isActive = i === activeIndex;
          const isFiltered = !filteredSet.has(i);
          const preview = msg.text.slice(0, 40) + (msg.text.length > 40 ? '...' : '');

          return (
            <button
              key={msg.id}
              className={`thread-outline__item${isActive ? ' thread-outline__item--active' : ''}${isFiltered ? ' thread-outline__item--filtered' : ''}`}
              onClick={() => onJump(i)}
              title={`${role}: ${msg.text.slice(0, 100)}`}
            >
              <span className="thread-outline__item-num">{i + 1}.</span>
              <span className="thread-outline__item-icon">{icon}</span>
              <span className="thread-outline__item-preview">{preview}</span>
              {isActive && <span className="thread-outline__item-indicator">←</span>}
            </button>
          );
        })}
      </div>

      {/* Related threads from other sources (M4C cross-pollination) */}
      {(relatedThreads?.length || relatedLoading) && (
        <>
          <button
            className="thread-outline__gallery-toggle"
            onClick={() => setRelatedOpen(!relatedOpen)}
            aria-expanded={relatedOpen}
          >
            <span className="thread-outline__gallery-chevron">{relatedOpen ? '▾' : '▸'}</span>
            <span className="thread-outline__gallery-label">
              Related {relatedLoading ? '...' : `(${relatedThreads?.length ?? 0})`}
            </span>
          </button>
          {relatedOpen && (
            <div className="thread-outline__related">
              {relatedLoading && !relatedThreads?.length && (
                <div className="thread-outline__related-loading">Finding related...</div>
              )}
              {relatedThreads?.map((rt) => (
                <button
                  key={rt.id}
                  className="thread-outline__related-item"
                  onClick={() => rt.threadRootId && onOpenRelated?.(rt.threadRootId)}
                  title={`${rt.sourceType} · ${Math.round(rt.score * 100)}% match\n${rt.preview}`}
                >
                  <span className="thread-outline__related-source">{rt.sourceType}</span>
                  <span className="thread-outline__related-title">{rt.title || rt.preview.slice(0, 50)}</span>
                  <span className="thread-outline__related-score">{Math.round(rt.score * 100)}%</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
